<template>
  <v-container class="pt-3 pb-6 px-6">

    <!-- Floating Button -->
    <floating-button @click="save()" save :disabled="invalid" v-if="authUser"/>

    <!-- Page Header -->
    <page-header
      :title="isNew + ' Cliente'"
      icon="mdi-view-list"
      divider="-"
      :breadcrumbs="breadcrumbs.items"
      refresh
      v-on:refresh="loadClients()"
    ></page-header>

    <v-row align="center" justify="space-around">
      <v-col cols="12">
        <v-card>
          <!-- Loading Bar -->
          <v-progress-linear
            indeterminate
            v-if="isLoadingData"
          ></v-progress-linear>
          <!-- Content -->
          <v-card-text v-if="authUser">
            <v-container>
              <v-row align="center" justify="start">
                <!-- Business Name -->
                <v-col cols="4">
                  <v-text-field
                    v-model="client.business_name"
                    :error-messages="businessNameErrors"
                    label="Razón social"
                    outlined
                    counter="100"
                    maxlength="100"
                    @blur="$v.client.business_name.$touch()"
                  ></v-text-field>
                </v-col>
                <!-- Brand Name -->
                <v-col cols="4">
                  <v-text-field
                    v-model="client.brand_name"
                    :error-messages="brandNameErrors"
                    label="Nombre comercial"
                    outlined
                    counter="100"
                    maxlength="100"
                    @blur="$v.client.brand_name.$touch()"
                  ></v-text-field>
                </v-col>
                <!-- State -->
                <v-col cols="4">
                  <v-select
                    v-model="client.is_active"
                    :error-messages="stateErrors"
                    :items="state"
                    label="Estado"
                    outlined
                    @blur="$v.client.is_active.$touch()"
                  >
                    <template v-slot:selection="data">
                      <v-chip
                        style="cursor: pointer"
                        :color="data.item.value ? 'primary' : 'deep-orange'"
                        :class="{'white--text': !data.item.value}"
                      >{{ data.item.text }}</v-chip>
                    </template>
                  </v-select>
                </v-col>
                <!-- User Client -->
                <v-col cols="4">
                  <v-autocomplete
                    v-model="client.users"
                    :items="users"
                    label="Clientes"
                    :item-text="text"
                    item-value="id"
                    outlined
                    multiple
                    chips
                    deletable-chips
                  ></v-autocomplete>
                </v-col>
                <!-- Client Farms -->
                <v-col cols="8">
                  <v-autocomplete
                    v-model="client.wind_farms"
                    :error-messages="farmErrors"
                    :items="farms"
                    item-text="name"
                    item-value="id"
                    label="Parques"
                    outlined
                    multiple
                    chips
                    deletable-chips
                    @blur="$v.client.wind_farms.$touch()"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import FloatingButton from "@/components/FloatingButton";
import PageHeader from "@/components/PageHeader";
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import { ClientRepository } from "@/api/Repositories";
import Util from "@/util/index";

export default {
  components: {
    FloatingButton,
    PageHeader
  },
  mixins: [validationMixin],
  validations: {
    client: {
      business_name: { required },
      brand_name: { required },
      is_active: { required },
      wind_farms: { required }
    }
  },
  data: () => ({
    authUser: false,
    isLoadingData: false,
    oldBusinessName: '',
    oldBrandName: '',
    client: {
      business_name: '',
      brand_name: '',
      is_active: '',
      wind_farms: []
    },
    state: [
      { text: 'Activo', value: true },
      { text: 'Inactivo', value: false }
    ],
    breadcrumbs: {
      items: [
        { text: "Configuración", disabled: true },
        { text: "Clientes", disabled: true }
      ]
    },
  }),
  computed: {
    loading: {
      get () {
        return this.$store.state.loading
      },
      set (val) {
        this.$store.commit('loading', val)
      }
    },
    isNew () {
      return this.$route.params.id ? 'Modificar' : 'Añadir'
    },
    invalid () {
      return this.$v.$invalid && this.$v.$dirty
    },
    farms () {
      return this.$store.state.farms.wind_farms.filter(element => {
        return element.is_active === true
      })
    },
    users () {
      return this.$store.state.users.all.filter(element => {
        return element.roles.filter(e => e.name === 'Cliente').length > 0 && element.isActive === true
      })
    },
    clients () {
      return this.$store.state.clients.clients
    },
    uniqueBusinessName () {
      if (this.client.business_name) {
        if (this.$route.params.id) {
          let nameExist = this.clients.some(element => {
            return element.business_name.trim() === this.client.business_name.trim()
              && element.business_name.trim() !== this.oldBusinessName.trim()
          })
          return !nameExist
        } else {
          let nameExist = this.clients.some(element => {
            return element.business_name.trim() === this.client.business_name.trim()
          })
          return !nameExist
        }
      } else {
        return true
      }
    },
    uniqueBrandName () {
      if (this.client.brand_name) {
        if (this.$route.params.id) {
          let nameExist = this.clients.some(element => {
            return element.brand_name.trim() === this.client.brand_name.trim()
              && element.brand_name.trim() !== this.oldBrandName.trim()
          })
          return !nameExist
        } else {
          let nameExist = this.clients.some(element => {
            return element.brand_name.trim() === this.client.brand_name.trim()
          })
          return !nameExist
        }
      } else {
        return true
      }
    },
    businessNameErrors () {
      const errors = []
      if (!this.$v.client.business_name.$dirty) return errors
      !this.$v.client.business_name.required && errors.push('Obligatorio.')
      !this.uniqueBusinessName && errors.push('Nombre ya existe.')
      return errors
    },
    brandNameErrors() {
      const errors = [];
      if (!this.$v.client.brand_name.$dirty) return errors;
      !this.$v.client.brand_name.required && errors.push("Obligatorio.");
      !this.uniqueBrandName && errors.push('Nombre ya existe.')
      return errors;
    },
    stateErrors () {
      const errors = []
      if (!this.$v.client.is_active.$dirty) return errors
      !this.$v.client.is_active.required && errors.push('Obligatorio.')
      return errors
    },
    farmErrors () {
      const errors = []
      if (!this.$v.client.wind_farms.$dirty) return errors
      !this.$v.client.wind_farms.required && errors.push('Obligatorio.')
      return errors
    }
  },
  methods: {
    text (item) {
      let name = item.firstName + ' ' + item.lastName1
      if (item.lastName2) name += ' ' + item.lastName2
      return name
    },
    clientsList () {
      this.$router.push({
        name: "client-list"
      });
    },
    save () {
      this.$route.params.id ? this.editClient() : this.createClient()
    },
    createClient () {
      this.$v.$touch()
      if (!this.$v.$invalid  && this.uniqueBusinessName && this.uniqueBrandName) {
        this.loading = true
        this.$store.dispatch('clients/createClient', this.client)
          .then(() => {
            this.$toast("Cliente creado correctamente.", {
              color: "success",
              x: "center",
              y: "top",
              icon: "mdi-information"
            });
            this.clientsList()
          })
          .catch(error => {
            var errorToastMessage = "Ha ocurrido un error creando el cliente."
            Util.showErrorToast(this, error, errorToastMessage);
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    editClient () {
      this.$v.$touch()
      if (!this.$v.$invalid && this.uniqueBusinessName && this.uniqueBrandName) {
        this.loading = true
        this.$store.dispatch('clients/editClient', this.client)
          .then(() => {
            this.$toast("Cliente modificado correctamente.", {
              color: "success",
              x: "center",
              y: "top",
              icon: "mdi-information"
            });
            this.clientsList()
          })
          .catch(error => {
            var errorToastMessage = "Ha ocurrido un error modificando el cliente."
            Util.showErrorToast(this, error, errorToastMessage);
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    loadClients () {
      this.isLoadingData = true
      this.$store.dispatch('clients/getClients')
        .then(() => {
          this.getClient()
        })
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando los clientes."
            Util.showErrorToast(this, error, errorToastMessage);
            this.clientsList()
          }
        })
    },
    getClient () {
      var id = this.$route.params.id
      if (id) {
        ClientRepository.get(id)
          .then(response => {
            let tempClient = response.data.data
            tempClient.wind_farms = tempClient.wind_farms.map(r => r.id);
            if (tempClient.users) tempClient.users = tempClient.users.map(r => r.id);

            this.client = tempClient
            this.oldBusinessName = tempClient.business_name
            this.oldBrandName = tempClient.brand_name
          })
          .catch(error => {
            if (error.response.status === 403 ) {
              this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
                color: "error",
                x: "center",
                y: "top",
                icon: "mdi-information",
                timeout: 0,
                showClose: true,
                closeText: "Cerrar"
              });
              this.$router.push({ name: 'home'})
            } else {
              var errorToastMessage = "Ha ocurrido un error cargando el cliente."
              Util.showErrorToast(this, error, errorToastMessage);
            }
            this.clientsList();
          })
      } else {
        this.client = {
          business_name: '',
          brand_name: '',
          is_active: '',
          wind_farms: []
        }
      }
      this.$v.$reset()
      this.authUser = true
      this.loadUsers()
    },
    loadUsers() {
      this.$store.dispatch("users/getAllUsers")
        .then(() => {})
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando la lista de operarios."
            Util.showErrorToast(this, error, errorToastMessage);
          }
        })
        .finally(() => {
          this.loadFarms()
        });
    },
    loadFarms () {
      this.$store.dispatch('farms/getFarms')
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando la lista de parques eólicos."
            Util.showErrorToast(this, error, errorToastMessage);
          }
        })
        .finally(() => {
          this.isLoadingData = false;
        });
    }
  },
  created () {
    this.loadClients()
  }
}
</script>
